//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { alpha, Chip, ChipProps } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TestableElement } from '@/__test__/testing_typings';

export type FilterChipProps = ChipProps & {
  label: ReactNode;
  isSelected: boolean;
  onClear?: ChipProps['onDelete'];
};

const FilterChip = ({ label, isSelected, onClear, onClick, ...props }: FilterChipProps) => {
  return (
    <StyledChip
      label={<FilterLabel isSelected={isSelected}>{label}</FilterLabel>}
      isSelected={isSelected}
      onClick={onClick}
      onDelete={isSelected && !!onClear ? onClear : undefined}
      deleteIcon={onClear ? <PolarisIcon data-testid={`clear-${(props as TestableElement)['data-testid']}`} icon="close" height={16} /> : undefined}
      {...props}
    />
  );
};

export default FilterChip;

const StyledChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'isSelected' })<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  height: 35,
  borderRadius: 25,
  border: '1px solid',
  borderColor: isSelected ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.text.primary, 0.2),
  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.16) : theme.palette.background.default,
  '&:hover': {
    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.32) : alpha(theme.palette.primary.main, 0.12)
  },
  '& svg.MuiChip-deleteIcon': {
    color: theme.palette.text.primary,
    marginRight: theme.spacing(1.5)
  }
}));

export const FilterLabel = styled('span', { shouldForwardProp: (prop) => prop !== 'isSelected' })<{ isSelected?: boolean }>(
  ({ theme, isSelected }) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.caption.fontSize,
    padding: theme.spacing(0, 0.5),
    color: isSelected ? theme.palette.primary.main : theme.palette.text.primary
  })
);
